import React, { useState, useEffect } from "react";
import { PaiementService } from "../../services/paiements/paiementService";
import { CreditCard, Search, CheckCircle, Clock, XCircle, FileText, Download, Send, BadgeCheck } from "lucide-react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { exportToCSV } from "../../utils/exportUtils";

interface Paiement {
  id: string;
  reference_paiement: string;
  montant: number;
  created_at: string;
  statut: "initie" | "demande_paiement" | "en_cours" | "execute" | "echec" | "annule";
  facture?: {
    numero: string;
    fournisseur_nom?: string;
  };
  beneficiaire_nom?: string;
  facture_montant_ttc?: number;
  facture_commission_succes?: number;
  facture_frais_escompte?: number;
  facture_montant_net?: number;
}

export const AdminPaiements: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchPaiements = async () => {
    try {
      setLoading(true);
      const response = await PaiementService.getPaiements(); 
      // Admin sees all payments due to backend role filtering (if implemented correctly)
      setPaiements(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement paiements", error);
      toast.error(t('paiements.messages.errorLoad')); // We might need to add this or reuse common
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaiements();
  }, []);

  const filteredPaiements = paiements.filter((p) => 
    p.reference_paiement?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.beneficiaire_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.facture?.fournisseur_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount: number) => new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(amount);

  const getStatusConfig = (statut: string) => {
    switch (statut) {
      case "execute": return { color: "bg-success-100 text-success-800", icon: CheckCircle, label: t('paiements.statuses.execute') };
      case "demande_paiement": return { color: "bg-orange-100 text-orange-800", icon: Send, label: t('paiements.statuses.demande_paiement') };
      case "en_cours": return { color: "bg-blue-100 text-blue-800", icon: Clock, label: t('paiements.statuses.en_cours') };
      case "initie": return { color: "bg-warning-100 text-warning-800", icon: Clock, label: t('paiements.statuses.initie') };
      case "echec": return { color: "bg-error-100 text-error-800", icon: XCircle, label: t('paiements.statuses.echec') };
      case "annule": return { color: "bg-gray-100 text-gray-600", icon: XCircle, label: t('paiements.statuses.annule') };
      default: return { color: "bg-gray-100 text-gray-800", icon: Clock, label: statut };
    }
  };

  if (loading && paiements.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  const handleExecuterPaiement = async (id: string, reference: string) => {
    if (!window.confirm(t('paiements.messages.confirmExecute', { ref: reference }))) {
      return;
    }

    try {
      setLoading(true);
      await PaiementService.executerPaiement(id);
      toast.success(t('paiements.messages.successExecute'));
      fetchPaiements();
    } catch (error) {
      console.error("Erreur exécution paiement", error);
      toast.error(t('paiements.messages.errorExecute'));
      setLoading(false);
    }
  };

  const handleConfirmerVirement = async (id: string, reference: string) => {
    const refBanque = window.prompt(
      t('paiements.messages.confirmVirementPrompt', { ref: reference })
    );
    if (refBanque === null) return; // User cancelled

    try {
      setLoading(true);
      await PaiementService.confirmerVirement(id, refBanque || undefined);
      toast.success(t('paiements.messages.successVirement'));
      fetchPaiements();
    } catch (error) {
      console.error("Erreur confirmation virement", error);
      toast.error(t('paiements.messages.errorVirement'));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('paiements.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('paiements.overview')}</p>
        </div>
        <button 
             onClick={() => exportToCSV(paiements, "admin_paiements")}
             className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
             <Download className="h-4 w-4 mr-2" /> {t('common.download')}
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
           <div className="relative max-w-xs">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  placeholder={t('header.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
           </div>
        </div>
        
        <div className="divide-y divide-gray-200">
            {filteredPaiements.length === 0 ? (
                <div className="p-8 text-center text-gray-500">{t('paiements.noPaymentFound')}</div>
            ) : (
                filteredPaiements.map((paiement) => {
                    const statusConfig = getStatusConfig(paiement.statut);
                    const StatusIcon = statusConfig.icon;
                    return (
                        <div key={paiement.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className={`p-2 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
                                            <CreditCard className={`h-6 w-6 ${statusConfig.color.split(' ')[1]}`} />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-gray-900">{paiement.reference_paiement}</span>
                                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                                <StatusIcon className="h-3 w-3 mr-1" /> {statusConfig.label}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm text-gray-500">
                                            {paiement.beneficiaire_nom || paiement.facture?.fournisseur_nom || t('common.unknown')}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right flex items-center space-x-3">
                                    <div className="text-sm font-bold text-gray-900">{formatCurrency(paiement.montant)}</div>
                                    {paiement.statut === 'initie' && (
                                        <button
                                            onClick={() => handleExecuterPaiement(paiement.id, paiement.reference_paiement)}
                                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                        >
                                            {t('paiements.execute')}
                                        </button>
                                    )}
                                    {paiement.statut === 'demande_paiement' && (
                                        <button
                                            onClick={() => handleConfirmerVirement(paiement.id, paiement.reference_paiement)}
                                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                        >
                                            <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                                            {t('paiements.confirmTransfer')}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 sm:flex sm:items-center sm:justify-between">
                                <div className="flex flex-col sm:flex-row sm:space-x-8 text-sm text-gray-500">
                                    <div className="flex items-center">
                                        <FileText className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                        {t('factures.title')}: <span className="font-medium ml-1 text-gray-900">{paiement.facture?.numero || "N/A"}</span>
                                    </div>
                                    {paiement.beneficiaire_nom && (
                                        <div className="mt-2 sm:mt-0 flex items-center">
                                            <BadgeCheck className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                            {t('paiements.beneficiary')}: <span className="ml-1 text-gray-900">{paiement.beneficiaire_nom}</span>
                                        </div>
                                    )}
                                    <div className="mt-2 sm:mt-0 flex items-center">
                                        <Clock className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                        {t('paiements.date')}: <span className="ml-1">{format(new Date(paiement.created_at), "dd MMM yyyy", { locale: i18n.language.startsWith('fr') ? fr : enUS })}</span>
                                    </div>
                                </div>
                                <div className="mt-4 sm:mt-0 flex space-x-2">
                                     {paiement.statut === 'initie' && (
                                         <button
                                             onClick={() => handleExecuterPaiement(paiement.id, paiement.reference_paiement)}
                                             className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                                         >
                                            {t('paiements.execute')}
                                         </button>
                                     )}
                                      {(paiement.statut === 'demande_paiement' || paiement.statut === 'en_cours') && (
                                         <button
                                             onClick={() => handleConfirmerVirement(paiement.id, paiement.reference_paiement)}
                                             className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                         >
                                            <CheckCircle className="h-4 w-4 mr-1 text-success-600" />
                                            {t('paiements.confirmReception')}
                                         </button>
                                     )}
                                </div>
                            </div>

                            {paiement.facture_montant_ttc != null && (
                                <div className="mt-4 bg-gray-50 rounded p-3 border border-gray-100 flex flex-col sm:flex-row justify-between items-center text-sm">
                                    <div className="flex space-x-4">
                                        <div className="text-gray-500">
                                            <span>{t('factures.title')} TTC: </span>
                                            <span className="font-medium text-gray-900">{formatCurrency(paiement.facture_montant_ttc)}</span>
                                        </div>
                                        {(Number(paiement.facture_commission_succes) > 0 || Number(paiement.facture_frais_escompte) > 0) && (
                                            <div className="text-red-500">
                                                <span>- {t('paiements.commissions')}: </span>
                                                <span className="font-medium">
                                                    {formatCurrency(Number(paiement.facture_commission_succes || 0) + Number(paiement.facture_frais_escompte || 0))}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-gray-900 mt-2 sm:mt-0">
                                        <span className="text-gray-500 mr-2">= {t('paiements.netToPay')}:</span>
                                        <span className="font-bold text-lg text-primary-600">{formatCurrency(paiement.montant)}</span>
                                    </div>
                                </div>
                            )}
                            {!paiement.facture_montant_ttc && (
                                <div className="mt-4 flex justify-end">
                                  <p className="font-bold text-lg text-gray-900">{formatCurrency(paiement.montant)}</p>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
      </div>
    </div>
  );
};
